'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthContext } from './AuthProvider';
import { createClient } from '@/lib/supabase/browser';

interface AdminViewContextType {
  isAdminViewEnabled: boolean;
  toggleAdminView: () => void;
  isAdmin: boolean;
  isLoading: boolean;
}

const AdminViewContext = createContext<AdminViewContextType | undefined>(undefined);

export function AdminViewProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthContext();
  const [isAdminViewEnabled, setIsAdminViewEnabled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated || !user) {
        setIsAdmin(false);
        setIsAdminViewEnabled(false);
        setIsLoading(false);
        return;
      }

      try {
        const supabase = createClient();
        // maybeSingle so a brand-new user (profile not yet propagated) gets
        // null instead of a PGRST116 throw — that race fired on every fresh
        // signup and noised up the console.
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('is_admin, role')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        } else {
          // Canonical source is the role column ('admin'|'super_admin' grant
          // admin status). Legacy is_admin boolean is honored as a fallback
          // for accounts that pre-date the role column.
          const role = profile?.role as string | undefined;
          const adminStatus =
            role === 'admin' || role === 'super_admin' || profile?.is_admin === true;
          setIsAdmin(adminStatus);
          // Reset admin view when user is not an admin
          if (!adminStatus) {
            setIsAdminViewEnabled(false);
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, isAuthenticated]);

  const toggleAdminView = () => {
    if (isAdmin) {
      setIsAdminViewEnabled(prev => !prev);
    }
  };

  return (
    <AdminViewContext.Provider
      value={{
        isAdminViewEnabled,
        toggleAdminView,
        isAdmin,
        isLoading
      }}
    >
      {children}
    </AdminViewContext.Provider>
  );
}

export function useAdminView() {
  const context = useContext(AdminViewContext);
  if (context === undefined) {
    throw new Error('useAdminView must be used within an AdminViewProvider');
  }
  return context;
}

